import React, { useState } from "react";
import styled, { keyframes } from "styled-components";

import leftBackground from "./assets/image/DivBG.svg";
import mobileBackground from "./assets/image/background-mobile.png";
import background1 from "./assets/image/background.png";
import background2 from "./assets/image/background1.png";
import background3 from "./assets/image/background2.png";
import texturedBackground from "./assets/image/bg-Texture.png";

import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/20/solid";

import video1Webm from "./assets/video/Tree_Anim01.webm";
import video2Webm from "./assets/video/Tree_Anim02.webm";
import video3Webm from "./assets/video/Tree_Anim03.webm";

import video1 from "./assets/video/animation1.mov";
import video2 from "./assets/video/animation2.mov";
import video3 from "./assets/video/animation3.mov";

import Slider from "react-slick";
import { slides } from "./data/companyLogos";

const sliderSettings = {
    dots: false,
    infinite: true,
    slidesToShow: 3,
    slidesToScroll: 1,
    autoplay: true,
    speed: 2500,
    autoplaySpeed: 2500,
    cssEase: "linear",
    arrows: false,
    pauseOnHover: false,
    responsive: [
        {
            breakpoint: 768,
            settings: {
                slidesToShow: 2,
            },
        },
    ],
};

function App() {
    const [currentStep, setCurrentStep] = useState(1);
    const [currentContentStep, setCurrentContentStep] = useState(0);

    const stepContent = {
        1: {
            title: "Scientific Research",
            contents: [
                {
                    subtitle: "Randomised Controlled Trials",
                    description: `RCTs to validate innovative approaches, solutions
                    and interventions in farming, forestry, health and
                    other social and evironmental contexts`,
                },
            ],
            background: background1,
            video: {
                mov: video1,
                webm: video1Webm,
            },
        },
        2: {
            title: "Human-Centered Solution Architecture & Building",
            contents: [
                {
                    subtitle: "Amaly Regenerative Ag Studio (ARAS)",
                    description: `Regenerative agriculture project design and implementation with a strong focus on carbon sequestration, yield enhancement, value-added processing and co-benefits for NGOs and communities`,
                },
                {
                    subtitle: "Tech 4 Good Solution Development",
                    description: `Architecture, design and development of tech solutions with a philanthropic nexus that unlock social and environmental impact`,
                },
                {
                    subtitle: "Financial Product Engineering",
                    description: `Retail banking product development with philanthropic and carbon credit components for banks, financial institutions, and consumer finance companies`,
                },
            ],
            background: background2,
            video: {
                mov: video2,
                webm: video2Webm,
            },
        },
        3: {
            title: "Market Entry & Distrbution",
            contents: [
                {
                    subtitle: "Pilots and Proof of Concepts",
                    description: `Designing pilots for social and environmental ventures to aid commercial growth with success metrics tied to CSR funding for NGOs`,
                },
                {
                    subtitle: "Strategic Partnerships & JVs",
                    description: `Designing multistakeholder private-public-philanthropic partnerships and JVs that achieve public policy, philanthropic, and commercial objectives`,
                },
                {
                    subtitle: "Exclusive Agency and Distribution",
                    description: `Agency and distribution agreements for ventures with family offices to access growth markets in the GCC and Africa with embedded charitable giving and sales`,
                },
            ],
            background: background3,
            video: {
                mov: video3,
                webm: video3Webm,
            },
        },
    };

    return (
        <Container backgroundImage={stepContent[currentStep]["background"]}>
            <LeftSection>
                <Spotlight />
                <Header>
                    <Heading>
                        <StepIndicator>{currentStep}</StepIndicator>
                        <HeaderTitle>
                            {stepContent[currentStep]["title"]}
                        </HeaderTitle>
                    </Heading>
                </Header>
                <Content>
                    <Title>
                        {
                            stepContent[currentStep]["contents"][
                                currentContentStep
                            ]["subtitle"]
                        }
                    </Title>
                    <Description>
                        {
                            stepContent[currentStep]["contents"][
                                currentContentStep
                            ]["description"]
                        }
                    </Description>
                </Content>
                <BulletPoints>
                    {stepContent[currentStep]["contents"]?.map(
                        (content, contentIndex) => (
                            <BulletPoint
                                key={contentIndex}
                                onClick={() =>
                                    setCurrentContentStep(contentIndex)
                                }
                                active={contentIndex === currentContentStep}
                            />
                        )
                    )}
                </BulletPoints>
                <CarouselContainer>
                    <CarouselTitle>Previous Experience</CarouselTitle>
                    <SliderContainer>
                        <Slider {...sliderSettings}>
                            {slides.map((slide) => (
                                <Logo src={slide.image} alt={slide.name} />
                            ))}
                        </Slider>
                    </SliderContainer>
                </CarouselContainer>
            </LeftSection>
            <RightSection>
                <ButtonContainer>
                    <BackButton onClick={() => window.history.back()}>
                        Back
                    </BackButton>
                </ButtonContainer>
                <VideoContainer>
                    <LeftNavWrapper
                        hide={currentStep === 1}
                        onClick={() => {
                            setCurrentStep((prevState) => prevState - 1);
                            setCurrentContentStep(0);
                        }}
                    >
                        <LeftNav />
                    </LeftNavWrapper>
                    <Video autoPlay loop muted playsInline key={currentStep}>
                        <source
                            src={stepContent[currentStep]["video"]["mov"]}
                            type='video/mp4; codecs="hvc1"'
                        />
                        <source
                            src={stepContent[currentStep]["video"]["webm"]}
                            type="video/webm"
                        />
                    </Video>
                    <RightNavWrapper
                        hide={currentStep === 3}
                        onClick={() => {
                            setCurrentStep((prevState) => prevState + 1);
                            setCurrentContentStep(0);
                        }}
                    >
                        <RightNav />
                    </RightNavWrapper>
                </VideoContainer>
            </RightSection>
        </Container>
    );
}

export default App;

const Container = styled.div`
    background-image: url(${texturedBackground}),
        ${({ backgroundImage }) => `url(${backgroundImage})`};
    background-repeat: no-repeat;
    background-size: cover;
    background-position: bottom;
    width: 100vw;
    min-height: 100vh;
    overflow: hidden;
    display: flex;

    @media (max-width: 768px) {
        flex-direction: column-reverse;
        background-position: top;
        background-image: url(${texturedBackground}), url(${mobileBackground});
    }
`;

const LeftSection = styled.div`
    background-image: url(${leftBackground});
    background-repeat: no-repeat;
    background-size: cover;
    background-position: center;
    flex-basis: 60%;
    min-height: 100vh;
    position: relative;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    padding-inline: 64px;
    gap: 64px;
    position: relative;

    @media (max-width: 768px) {
        padding-inline: 16px;
        background: none;
        justify-content: flex-start;
        gap: 16px;
        min-height: auto;
        padding-bottom: 24px;
    }
`;

const Spotlight = styled.div`
    height: 400px;
    width: 400px;
    background: rgba(52, 111, 142, 0.5);
    border-radius: 50%;
    filter: blur(100px);
    position: absolute;
    top: 10%;
    left: 0;
    animation: spotlight 2s ease-in-out infinite alternate;

    @keyframes spotlight {
        0% {
            transform: translate(0%, 0%, 0%);
            top: 10%;
            left: 0;
        }

        50% {
            transform: translate(-50%, -50%);
            top: 50%;
            left: 50%;
        }

        100% {
            transform: translate(0%, -100%);
            top: 100%;
            left: 0;
        }
    }

    @media (max-width: 768px) {
        background: rgba(33, 70, 89, 0.65);
    }
`;

const RightSection = styled.div`
    flex-basis: 40%;
    display: flex;
    flex-direction: column;

    @media (max-width: 768px) {
        padding-inline: 16px;
        align-items: center;
        min-height: 400px;
    }
`;

const Header = styled.div`
    display: flex;
    flex-direction: column;
    max-width: 700px;
`;

const Heading = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
`;

const StepIndicator = styled.h1`
    font-family: "Stick No Bills";
    font-size: 200px;
    color: rgba(255, 255, 255, 0.3);
    font-weight: 400;

    @media (max-width: 768px) {
        font-size: 96px;
    }
`;

const HeaderTitle = styled.div`
    font-size: 40px;
    font-weight: 800;
    color: rgba(255, 255, 255, 0.3);

    @media (max-width: 768px) {
        font-size: 24px;
    }
`;

const Content = styled.div`
    display: flex;
    flex-direction: column;
    max-width: 500px;
    gap: 16px;
`;

const Title = styled.p`
    font-size: 24px;
    font-weight: 800;
    color: #ffffff;
`;

const Description = styled.p`
    color: #ffffff;
`;

const ButtonContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    width: 80%;
    margin-top: 48px;

    @media (max-width: 768px) {
        margin-top: 24px;
        width: 90%;
    }
`;

const BackButton = styled.button`
    border-radius: 30px;
    border: 1px solid #fafafa;
    background: transparent;
    color: #fafafa;
    box-sizing: border-box;
    width: 81px;
    height: 40px;
    cursor: pointer;
`;

const VideoContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    margin-block: auto;
    margin-inline: auto;
    position: relative;
    width: 500px;

    @media (max-width: 768px) {
        width: auto;
    }
`;

const LeftNavWrapper = styled.div`
    outline: 4px solid rgba(52, 111, 142, 1);
    height: 48px;
    width: 48px;
    cursor: pointer;
    visibility: ${({ hide }) => (hide ? "hidden" : "visible")};
    border-radius: 50%;
    display: grid;
    place-items: center;
    overflow: hidden;
    position: absolute;
    top: 50%;
    left: 0;
    transform: translateY(-50%);
    z-index: 100;
`;

const growShrinkGlow = keyframes`
    from {
        box-shadow: 0px 0px 8.62px 0px rgba(76, 199, 255, 1) inset;
        -webkit-box-shadow: 0px 0px 8.62px 0px rgba(76, 199, 255, 1) inset;
        transform: scale(1);
    }

    to {
        filter: drop-shadow(0px 0px 17.62px rgba(239, 243, 245, 1));
        transform: scale(1.2);
    }
`;

const LeftNav = styled(ChevronLeftIcon)`
    height: 32px;
    width: 32px;
    color: #ffffff;
    background: rgba(52, 111, 142, 1);
    border-radius: 50%;
    animation: ${growShrinkGlow} 2s infinite alternate ease-in-out;
`;

const RightNavWrapper = styled.div`
    outline: 4px solid rgba(52, 111, 142, 1);
    height: 48px;
    width: 48px;
    cursor: pointer;
    visibility: ${({ hide }) => (hide ? "hidden" : "visible")};
    border-radius: 50%;
    display: grid;
    place-items: center;
    overflow: hidden;
    position: absolute;
    top: 50%;
    right: 0;
    transform: translateY(-50%);
    z-index: 100;
`;

const RightNav = styled(ChevronRightIcon)`
    height: 32px;
    width: 32px;
    color: #ffffff;
    background: rgba(52, 111, 142, 1);
    border-radius: 50%;
    animation: ${growShrinkGlow} 2s infinite alternate ease-in-out;
`;

const Video = styled.video`
    max-width: 400px;
    margin-block: auto;

    @media (max-width: 768px) {
        width: 380px;
        max-width: 100%;
    }
`;

const BulletPoints = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
`;

const BulletPoint = styled.div`
    width: 30px;
    height: 30px;
    border-radius: 50%;
    cursor: pointer;

    ${({ active }) =>
        active
            ? `
    background: rgba(52, 111, 142, 1);
    box-shadow: 0px 0px 6.16px 0px rgba(76, 199, 255, 1) inset;
    --webkit-box-shadow: 0px 0px 6.16px 0px rgba(76, 199, 255, 1) inset;`
            : `background: rgba(52, 111, 142, 0.02);
    box-shadow: 0px 0px 6.16px 0px rgba(76, 199, 255, 1) inset;
    --webkit-box-shadow: 0px 0px 6.16px 0px rgba(76, 199, 255, 1) inset;
    box-shadow: 0px 0px 12.58px 0px rgba(239, 243, 245, 1);
    --webkit-box-shadow: 0px 0px 12.58px 0px rgba(239, 243, 245, 1);
    animation: growAndShrink 1s infinite alternate;
    `}

    @keyframes growAndShrink {
        from {
            transform: scale(1);
        }

        to {
            transform: scale(1.1);
        }
    }
`;

const CarouselContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;

    @media (max-width: 768px) {
        gap: 0;
    }
`;

const CarouselTitle = styled.div`
    font-size: 24px;
    color: rgba(255, 255, 255, 0.3);
`;

const SliderContainer = styled.div`
    max-width: 70%;

    @media (max-width: 768px) {
        max-width: 100%;
    }
`;

const Logo = styled.img``;
